import { createContext, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getModuleBySlug, getSubModulesForModule, Module } from '../../../api/module';
import useThrowAsyncError from '../../../hooks/useThrowAsyncError';
import { markModuleAsVisited } from '../api/materialApi';

export type ModuleContextValue = {
  module?: Module;
  subModules: Module[];
  allModules: Module[];
  selectedModule?: Module;
  findBySlug: (slug?: string) => Module | undefined;
  refetch: () => void;
};

const ModuleContext = createContext<ModuleContextValue>({
  module: undefined,
  subModules: [],
  allModules: [],
  selectedModule: undefined,
  findBySlug: () => undefined,
  refetch: () => {},
});

export default ModuleContext;

export function ModuleContextProvider({ children }: PropsWithChildren) {
  const { REACT_APP_P_STUDIO_GROUP } = process.env;

  const { moduleSlug, selectedModuleSlug } = useParams();

  const handleError = useThrowAsyncError();

  const [module, setModule] = useState<Module>();
  const [subModules, setSubModules] = useState<Module[]>([]);
  const [selectedModule, setSelectedModule] = useState<Module>();
  const allModules = (module ? [module] : []).concat(subModules);

  const findBySlug = useCallback(
    (slug?: string) => (slug ? allModules.find(m => m?.slug === slug) : undefined),
    [allModules],
  );

  const fetchModule = useCallback(async () => {
    if (!moduleSlug) return;

    try {
      const { data: module } = await getModuleBySlug(moduleSlug);

      const [{ data: subModules }] = await Promise.all([
        getSubModulesForModule(module.id),
        markModuleAsVisited(module.id),
      ]);

      setSubModules(subModules);

      return setModule(module);
    } catch (e) {
      handleError(e);
    }
  }, [moduleSlug, handleError]);

  useEffect(() => {
    fetchModule();
  }, [fetchModule]);

  useEffect(() => {
    if (selectedModuleSlug) {
      setSelectedModule(findBySlug(selectedModuleSlug));
    } else {
      setSelectedModule(undefined);
    }
  }, [findBySlug, selectedModuleSlug]);

  return (
    <ModuleContext.Provider
      value={{
        module,
        subModules,
        allModules,
        findBySlug,
        selectedModule,
        refetch: fetchModule,
      }}
    >
      {module?.styling?.primaryColor && (
        <style>
          {`:root .body--${REACT_APP_P_STUDIO_GROUP.toLowerCase()} { --module-color: ${module.styling.primaryColor}}`}
        </style>
      )}
      {children}
    </ModuleContext.Provider>
  );
}
